import { createBrowserHistory } from 'history';
import {
  ADMIN_ORIGIN,
  IS_ADMIN_HOST,
  MERCHANT_HOSTS,
  MERCHANT_ORIGIN,
} from 'utils/envVars';

// Steering staff between the merchant host and crew. The backend decides who is
// staff and which PS Labs flags are on for the current shop (/me admin_host);
// this module adds the host, stay-marker and URL rules.

export interface AdminHost {
  has_admin_access: boolean;
  show_banner: boolean;
  should_redirect: boolean;
}

export const isMerchantHost = (hostname: string = window.location.hostname) =>
  (Object.values(MERCHANT_HOSTS) as string[]).includes(hostname);

export const STAY_PARAM = 'stay';
export const STAY_MS = 30 * 60 * 1000;
const STAY_KEY = 'ps_admin_host_stay_until';
// Set when this page load consumed stay=1, so the stay holds for this load even
// when session storage is blocked.
let stayUntilThisLoad = 0;

// ?stay=1 pauses steering in this tab for 30 minutes. The parameter is removed
// so it isn't copied into shared links.
export const consumeStayParam = (now: number = Date.now()) => {
  const url = new URL(window.location.href);
  if (url.searchParams.get(STAY_PARAM) !== '1') return;
  stayUntilThisLoad = now + STAY_MS;
  try {
    sessionStorage.setItem(STAY_KEY, String(now + STAY_MS));
  } catch {
    // Storage blocked: the stay holds for this page load only.
  }
  // Re-serializing the query through URLSearchParams is accepted: it may
  // re-encode other parameters ('%20' to '+'), but their values parse the same.
  url.searchParams.delete(STAY_PARAM);
  try {
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Never block app start-up; the marker is already set and stay=1 just stays
    // in the address bar.
  }
};

// The app's router history. stay=1 is consumed first, so the router is created
// from the address without it and can't write it back into the URL.
export const createAppHistory = () => {
  consumeStayParam();
  return createBrowserHistory();
};

export const isStaying = (now: number = Date.now()) => {
  if (stayUntilThisLoad > now) return true;
  try {
    const until = Number(sessionStorage.getItem(STAY_KEY));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
};

// The path can be attacker-shaped ('//evil.com/x'), so force the origin.
export const onOrigin = (origin: string, pathAndQuery: string) => {
  const url = new URL(`/${pathAndQuery.replace(/^[/\\]+/, '')}`, origin);
  return url.origin === origin ? url : new URL('/', origin);
};

// For navigating to a path taken from the address (the post-login or
// post-switch target): '//evil.com/x' would otherwise leave this origin.
export const sameOriginHref = (
  pathAndQuery: string,
  origin: string = window.location.origin,
) => onOrigin(origin, pathAndQuery).href;

// The same, as a path for an in-app router redirect: the router can't navigate
// off origin, but replaceState throws on a cross-origin URL and blanks the app.
export const sameOriginPath = (
  pathAndQuery: string,
  origin: string = window.location.origin,
) => {
  const { pathname, search, hash } = onOrigin(origin, pathAndQuery);
  return `${pathname}${search}${hash}`;
};

export const merchantUrl = (pathAndQuery: string) => {
  const url = onOrigin(MERCHANT_ORIGIN, pathAndQuery);
  // Re-serializing the query through URLSearchParams is accepted: other
  // parameters may be re-encoded, but their values parse the same.
  url.searchParams.set(STAY_PARAM, '1');
  return url.href;
};

export const isFramed = () => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent: treat as framed. Cloudflare Access can't log in inside a frame.
    return true;
  }
};

// Never redirected: routes that do a one-shot side effect as they load (Shopify
// auth and init post or redirect with single-use codes; the Zendesk
// authorization forwarder), the logged-out pages around login, and the
// shopper-facing order page.
const EXCLUDED_PATHS = [
  '/shopify',
  '/integrations/zendesk/oauth_authorize',
  '/pspay-customer',
  '/login',
  '/login_email',
  '/new_password',
  '/reset',
  '/request_reset',
  '/register_store',
  '/ref',
];

const ABSOLUTE_URL = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

export const isExcludedFromRedirect = (url: URL) => {
  const { searchParams: q } = url;
  // The router matches paths case-insensitively, so compare the same way.
  const pathname = url.pathname.toLowerCase();
  if (
    EXCLUDED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  ) {
    return true;
  }
  if (q.has('charge_id')) return true;
  if (q.has('code') && (q.has('hmac') || q.has('state'))) return true;
  const next = q.get('next');
  return Boolean(next && ABSOLUTE_URL.test(next));
};

export const adminRedirectUrl = (
  pathAndQuery: string,
  swroId?: string | null,
) => {
  const url = onOrigin(ADMIN_ORIGIN, pathAndQuery);
  // Re-serializing the query through URLSearchParams is accepted, as in merchantUrl.
  url.searchParams.delete(STAY_PARAM);
  if (swroId) url.searchParams.set('swro', swroId);
  return url.href;
};

// A link to this page for sending outside the company. Crew links only open for
// staff, so on crew it points at the merchant host. No swro: a merchant opens it
// in their own shop, and the link reveals no shop id. None on an excluded
// address: one-shot parameters such as Shopify's code and hmac must not be shared.
export const merchantShareUrl = (
  href: string,
  isAdminHost: boolean = IS_ADMIN_HOST,
) => {
  const current = new URL(href);
  if (isExcludedFromRedirect(current)) return null;
  const url = isAdminHost
    ? onOrigin(
        MERCHANT_ORIGIN,
        `${current.pathname}${current.search}${current.hash}`,
      )
    : current;
  // Re-serializing the query through URLSearchParams is accepted, as in merchantUrl.
  url.searchParams.delete(STAY_PARAM);
  url.searchParams.delete('swro');
  return url.href;
};

// Rewrites the links in copied HTML that point at this app (relative, this
// origin, crew or the merchant host) through merchantShareUrl. Other links are
// left alone; an internal link that can't be shared, or an in-page anchor, keeps
// its text but loses its href. count is the number of internal links found.
export const withMerchantLinks = (
  html: string,
  baseHref: string,
  isAdminHost: boolean = IS_ADMIN_HOST,
) => {
  const internal = [new URL(baseHref).origin, ADMIN_ORIGIN, MERCHANT_ORIGIN];
  // A template parses inertly: no scripts run and no images load.
  const template = document.createElement('template');
  template.innerHTML = html;
  let count = 0;
  let rewritten = 0;
  template.content.querySelectorAll('a[href]').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    // '#' and in-page anchors (onClick-only table names) would resolve to this
    // page, and the clipboard resolves them against crew.
    if (href === '' || href.startsWith('#')) {
      link.removeAttribute('href');
      return;
    }
    let target: URL;
    try {
      target = new URL(href, baseHref);
    } catch {
      return;
    }
    if (!internal.includes(target.origin)) return;
    count += 1;
    const shareUrl = merchantShareUrl(
      target.href,
      isAdminHost || target.origin === ADMIN_ORIGIN,
    );
    if (shareUrl) {
      link.setAttribute('href', shareUrl);
      rewritten += 1;
    } else {
      link.removeAttribute('href');
    }
  });
  return { html: template.innerHTML, count, rewritten };
};

export const decideOnLoad = (adminHost: AdminHost | undefined, url: URL) =>
  Boolean(adminHost?.should_redirect) &&
  isMerchantHost(url.hostname) &&
  !isFramed() &&
  !isStaying() &&
  !isExcludedFromRedirect(url);

// Only a positive integer of sane length: the raw value is shown in the confirm
// prompt.
export const parseShopId = (id: string | null | undefined) =>
  id && /^\d{1,12}$/.test(id) && Number(id) > 0 ? id : null;

// The swro id to hold from the landing address, captured before the app
// changes the address. None on an excluded address: AppAuth pushes /dashboard
// before the first /me resolves, so the later exclusion check would miss a
// one-shot return such as /shopify/auth?code=...&swro=N.
export const swroToHold = (url: URL) =>
  isExcludedFromRedirect(url)
    ? null
    : parseShopId(url.searchParams.get('swro'));

export const decideSwitcheroo = (
  id: string | null | undefined,
  adminHost: AdminHost | undefined,
  isAdminHost: boolean,
): 'ignore' | 'confirm' | 'redirect' | 'switch' => {
  if (!parseShopId(id)) return 'ignore';
  if (isAdminHost) return 'confirm';
  if (adminHost?.should_redirect && isMerchantHost() && !isFramed())
    return 'redirect';
  return 'switch';
};

// The query string with every swro removed, for cleaning the address bar.
export const withoutSwro = (search: string) => {
  const params = new URLSearchParams(search);
  if (!params.has('swro')) return search;
  params.delete('swro');
  const query = params.toString();
  return query ? `?${query}` : '';
};

export type FirstMePlan =
  | { type: 'redirect'; url: string }
  | { type: 'confirm'; id: string }
  | { type: 'switch'; id: string }
  | { type: 'none' };

// What the first successful /me of a page load should do. adminHost is
// undefined when the backend predates admin_host: a swro link then confirms on
// crew and switches elsewhere, and nothing redirects. A failed /me never
// reaches here; the caller waits for a successful one.
//
// Deliberate: swro ignores the stay marker, which anyone can set through a
// link, so ?stay=1&swro=N can't switch silently on the merchant host. An
// excluded address (current or redirect target) drops swro instead, so a
// one-shot return such as /shopify/auth?code=... is neither carried to crew
// nor reloaded by a switch.
export const planFirstMe = ({
  id,
  adminHost,
  isAdminHost,
  href,
  redirectTo,
}: {
  id: string | null | undefined;
  adminHost: AdminHost | undefined;
  isAdminHost: boolean;
  href: string;
  redirectTo: string | null | undefined;
}): FirstMePlan => {
  const current = new URL(href);
  const target = redirectTo ? onOrigin(current.origin, redirectTo) : current;
  const excluded =
    isExcludedFromRedirect(current) || isExcludedFromRedirect(target);
  const destination =
    redirectTo || `${current.pathname}${current.search}${current.hash}`;
  const shopId = parseShopId(id);

  if (shopId) {
    if (excluded) return { type: 'none' };
    const action = decideSwitcheroo(shopId, adminHost, isAdminHost);
    if (action === 'confirm') return { type: 'confirm', id: shopId };
    if (action === 'redirect') {
      return { type: 'redirect', url: adminRedirectUrl(destination, shopId) };
    }
    return { type: 'switch', id: shopId };
  }
  if (!excluded && decideOnLoad(adminHost, current)) {
    return { type: 'redirect', url: adminRedirectUrl(destination) };
  }
  return { type: 'none' };
};
